import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Eye, EyeOff, CheckCircle, XCircle,
  Shield, Loader2, Database, Trash2, Flame
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const encryptCredentials = (creds) => btoa(JSON.stringify(creds));
const decryptCredentials = (encrypted) => {
  try { return JSON.parse(atob(encrypted)); } catch { return null; }
};

const DatabricksConnect = ({ isOpen, onClose, onConnected }) => {
  const [credentials, setCredentials] = useState({
    workspace_url: '',
    access_token: '',
    account_label: ''
  });
  const [showToken, setShowToken] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [rememberCredentials, setRememberCredentials] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem('databricks_creds');
    if (saved) {
      const decrypted = decryptCredentials(saved);
      if (decrypted) {
        setCredentials(decrypted);
        setRememberCredentials(true);
      }
    }
  }, []);

  const handleConnect = async () => {
    if (!credentials.workspace_url || !credentials.access_token) {
      toast.error('Workspace URL and Personal Access Token are required');
      return;
    }
    setIsConnecting(true);
    setConnectionStatus(null);
    try {
      // Validate with the audit endpoint (probes /api/2.0/preview/scim/v2/Me)
      const response = await axios.post(`${API_URL}/api/cloud-audit/databricks`, credentials);
      if (response.data?.provider === 'Databricks') {
        setConnectionStatus({
          success: true,
          account_id: response.data.account_id,
          resources_scanned: response.data.resources_scanned,
          note: response.data.note
        });
        if (rememberCredentials) {
          localStorage.setItem('databricks_creds', encryptCredentials(credentials));
        } else {
          localStorage.removeItem('databricks_creds');
        }
        sessionStorage.setItem('databricks_session', encryptCredentials(credentials));
        toast.success('Connected to Databricks workspace!');
        onConnected?.(credentials, response.data);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Connection failed';
      setConnectionStatus({ success: false, error: message });
      toast.error(message);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleClearSaved = () => {
    localStorage.removeItem('databricks_creds');
    sessionStorage.removeItem('databricks_session');
    setCredentials({ workspace_url: '', access_token: '', account_label: '' });
    setRememberCredentials(false);
    setConnectionStatus(null);
    toast.success('Saved credentials cleared');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="databricks-connect-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Flame className="w-5 h-5 text-[#FF3621]" />
            Connect to Databricks
          </DialogTitle>
          <DialogDescription>
            Connect via Personal Access Token (PAT) — read-only API access only.
          </DialogDescription>
        </DialogHeader>

        <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-3 mb-4">
          <div className="flex items-start gap-2">
            <Shield className="w-5 h-5 text-emerald-500 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                Your data stays secure
              </p>
              <p className="text-xs text-emerald-600 dark:text-emerald-400">
                Token is stored only in your browser. We query Databricks REST APIs directly —
                <strong> no data is ever stored on our servers.</strong>
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="workspace_url">Workspace URL *</Label>
            <Input
              id="workspace_url"
              placeholder="https://adb-1234.5.azuredatabricks.net"
              value={credentials.workspace_url}
              onChange={(e) => setCredentials({ ...credentials, workspace_url: e.target.value })}
              data-testid="databricks-workspace-url"
            />
            <p className="text-xs text-slate-400">
              The base URL of your Databricks workspace (Azure, AWS, or GCP-hosted).
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="access_token">Personal Access Token (PAT) *</Label>
            <div className="relative">
              <Input
                id="access_token"
                type={showToken ? 'text' : 'password'}
                placeholder="dapi••••••••••••••••"
                value={credentials.access_token}
                onChange={(e) => setCredentials({ ...credentials, access_token: e.target.value })}
                data-testid="databricks-access-token"
              />
              <button
                type="button"
                onClick={() => setShowToken(!showToken)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-400"
              >
                {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-slate-400">
              Create one in Databricks: <strong>User Settings → Access tokens → Generate new token</strong>.
              Workspace Admin role is recommended for full visibility.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="account_label">Workspace Label (optional)</Label>
            <Input
              id="account_label"
              placeholder="Production / Staging / Dev"
              value={credentials.account_label}
              onChange={(e) => setCredentials({ ...credentials, account_label: e.target.value })}
            />
          </div>

          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={rememberCredentials}
                onChange={(e) => setRememberCredentials(e.target.checked)}
                className="rounded border-slate-300"
              />
              <span className="text-sm text-slate-400 dark:text-slate-400">
                Remember credentials (browser only)
              </span>
            </label>
            {localStorage.getItem('databricks_creds') && (
              <Button variant="ghost" size="sm" onClick={handleClearSaved} className="text-red-500">
                <Trash2 className="w-4 h-4 mr-1" />
                Clear saved
              </Button>
            )}
          </div>

          {connectionStatus && (
            <div className={`p-3 rounded-lg border ${
              connectionStatus.success
                ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800'
                : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'
            }`}>
              <div className="flex items-start gap-2">
                {connectionStatus.success ? (
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-500" />
                )}
                <div>
                  <p className={`text-sm font-medium ${
                    connectionStatus.success ? 'text-emerald-700' : 'text-red-700'
                  }`}>
                    {connectionStatus.success ? 'Connected Successfully' : 'Connection Failed'}
                  </p>
                  {connectionStatus.success ? (
                    <div className="text-xs text-emerald-600 dark:text-emerald-400 mt-1 space-y-0.5">
                      <p>Workspace: {connectionStatus.account_id}</p>
                      <p>Resources scanned: {connectionStatus.resources_scanned}</p>
                      {connectionStatus.note && <p className="opacity-75">{connectionStatus.note}</p>}
                    </div>
                  ) : (
                    <p className="text-xs text-red-600">{connectionStatus.error}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleConnect}
            disabled={isConnecting}
            className="bg-[#FF3621] hover:bg-[#cc2d1b] text-white"
            data-testid="databricks-connect-submit"
          >
            {isConnecting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Connecting...</>
            ) : (
              <><Database className="w-4 h-4 mr-2" />Connect</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const useDatabricksCredentials = () => {
  const [credentials, setCredentials] = useState(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    const sessionCreds = sessionStorage.getItem('databricks_session');
    if (sessionCreds) {
      const decrypted = decryptCredentials(sessionCreds);
      if (decrypted) {
        setCredentials(decrypted);
        setIsConnected(true);
        return;
      }
    }
    const savedCreds = localStorage.getItem('databricks_creds');
    if (savedCreds) {
      const decrypted = decryptCredentials(savedCreds);
      if (decrypted) {
        setCredentials(decrypted);
        setIsConnected(true);
      }
    }
  }, []);

  const disconnect = () => {
    sessionStorage.removeItem('databricks_session');
    setCredentials(null);
    setIsConnected(false);
  };

  const updateCredentials = (newCreds) => {
    setCredentials(newCreds);
    setIsConnected(true);
    sessionStorage.setItem('databricks_session', encryptCredentials(newCreds));
  };

  return { credentials, isConnected, disconnect, updateCredentials };
};

export default DatabricksConnect;
